import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  CategoryScale,
} from 'chart.js';
import { Chip } from '@mui/material';

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  CategoryScale
);

function Graph() {
  const { broadcastId } = useParams();
  const [warmupPlans, setWarmupPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleDomain, setVisibleDomain] = useState(null);
  const [isDomainSelected, setIsDomainSelected] = useState(false);

  useEffect(() => {
    if (!broadcastId) return;

    const fetchWarmupPlans = async () => {
      const token = localStorage.getItem('prae.jwt');
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/broadcasts/warmup/plan/${broadcastId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        if (!response.ok) {
          throw new Error(`Erreur HTTP : ${response.status}`);
        }
        const data = await response.json();
        const sortedPlans = data.warmupPlans
          .sort(
            (a, b) =>
              b.daily_plan.reduce((sum, v) => sum + v, 0) -
              a.daily_plan.reduce((sum, v) => sum + v, 0)
          )
          .slice(0, 10);
        setWarmupPlans(sortedPlans);
        setVisibleDomain(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWarmupPlans();
  }, [broadcastId]);

  if (!broadcastId) {
    return <p>Aucun broadcast sélectionné.</p>;
  }

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error : {error}</p>;
  if (!warmupPlans.length) return <p>Aucun plan de warmup disponible.</p>;

  const numDays = warmupPlans[0].daily_plan.length;
  const labels = Array.from({ length: numDays }, (_, i) => `${i + 1}`);

  const datasets = warmupPlans
    .filter(plan =>
      visibleDomain ? plan.email_domain === visibleDomain : true
    )
    .map((plan, index) => ({
      label: plan.email_domain,
      data: plan.daily_plan,
      borderColor: `hsl(${index * 30}, 70%, 60%)`,
      backgroundColor: `hsl(${index * 30}, 70%, 80%)`,
      borderWidth: 2,
      pointRadius: 2,
      fill: false,
    }));

  const handleDomainClick = domain => {
    if (visibleDomain === domain) {
      setVisibleDomain(null);
      setIsDomainSelected(false);
    } else {
      setVisibleDomain(domain);
      setIsDomainSelected(true);
    }
  };

  const handleReset = () => {
    setVisibleDomain(null);
    setIsDomainSelected(false);
  };

  const data = {
    labels,
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Jours',
        },
      },
      y: {
        title: {
          display: true,
          text: "Volume d'emails",
        },
      },
    },
  };

  return (
    <div style={{ width: '100%', height: '300px' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '10px',
        }}
      >
        {warmupPlans.map((plan, index) => (
          <Chip
            key={plan.email_domain}
            label={plan.email_domain}
            style={{
              backgroundColor: `hsl(${index * 30}, 70%, 80%)`,
              color: `hsl(${index * 30}, 70%, 30%)`,
              cursor: 'pointer',
              border:
                visibleDomain === plan.email_domain
                  ? '2px solid black'
                  : 'none',
            }}
            onClick={() => handleDomainClick(plan.email_domain)}
          />
        ))}

        {isDomainSelected && (
          <Chip
            label="X"
            onClick={handleReset}
            style={{
              backgroundColor: 'red',
              color: 'white',
              cursor: 'pointer',
              marginLeft: '8px',
              fontWeight: 'bold',
              padding: '4px',
            }}
          />
        )}
      </div>

      <Line data={data} options={options} />
    </div>
  );
}

export default Graph;
